import { getToken } from 'data/auth/utils'
import { SearchFunction } from 'entities-next/searchFunctions'
import { isNil } from 'lodash'
import { SelectableOption } from 'scenes/Dashboard/components/AssessmentsView/components/AssessmentTable/components/AssessmentRow'
import { createApiHandler, CreateApiHandlerConfig } from 'services-next/api'
import { parsePaginationOptions } from 'utils-next/api'
import { PaginationOptions } from 'utils-next/types'

import { SearchCandidate, SearchCandidatesFilters } from './SearchCandidates.types'

const SEARCH_FUNCTION_ID_PATH_SELECTOR = ':searchFunctionId'
const SEARCH_CANDIDATES_ENDPOINT_PATH = `/admin/screening/search-functions/${SEARCH_FUNCTION_ID_PATH_SELECTOR}/search-candidates`

const SEARCH_CANDIDATE_ID_PATH_SELECTOR = ':searchFunctionId'
const SINGLE_SEARCH_CANDIDATE_ENDPOINT_PATH = `${SEARCH_CANDIDATES_ENDPOINT_PATH}/${SEARCH_CANDIDATE_ID_PATH_SELECTOR}`

export function getApiSearchCandidatesHandlers(config: Omit<CreateApiHandlerConfig, 'baseUrl'> = {}) {
  const token = getToken() || undefined
  const { callApi } = createApiHandler(token, config)

  async function apiGetSearchCandidates(
    { searchFunctionId, ...restFilters }: SearchCandidatesFilters,
    paginationOptions: PaginationOptions
  ): Promise<SearchCandidate[]> {
    const response = await callApi<WrappedList<SearchCandidate>>({
      url: SEARCH_CANDIDATES_ENDPOINT_PATH.replace(SEARCH_FUNCTION_ID_PATH_SELECTOR, String(searchFunctionId)),
      params: { ...parsePaginationOptions(paginationOptions), ...restFilters },
    })
    return formatIsUsefulProperty(response.content)
  }

  function formatIsUsefulProperty(searchCandidate: SearchCandidate[]): SearchCandidate[] {
    return searchCandidate.map((e) => {
      e.isUseful = isNil(e.isUseful)
        ? SelectableOption.Unknown
        : e.isUseful
        ? SelectableOption.Yes
        : SelectableOption.No
      return e
    })
  }

  async function apiGenerateSearchCandidatesForSearchFunction(searchFunctionId: SearchFunction['id']): Promise<void> {
    return callApi<void>({
      method: 'post',
      url: SEARCH_CANDIDATES_ENDPOINT_PATH.replace(SEARCH_FUNCTION_ID_PATH_SELECTOR, String(searchFunctionId)),
    })
  }

  async function apiPatchSearchCandidate(patchableSearchCandidate: PatchableSearchCandidate): Promise<void> {
    const { searchFunctionId, searchCandidateId, ...data } = patchableSearchCandidate
    return callApi<void>({
      method: 'patch',
      url: SINGLE_SEARCH_CANDIDATE_ENDPOINT_PATH.replace(
        SEARCH_FUNCTION_ID_PATH_SELECTOR,
        String(searchFunctionId)
      ).replace(SEARCH_CANDIDATE_ID_PATH_SELECTOR, String(searchCandidateId)),
      data,
    })
  }

  return { apiGetSearchCandidates, apiGenerateSearchCandidatesForSearchFunction, apiPatchSearchCandidate }
}

type WrappedList<R> = {
  content: R[]
}

type PatchableSearchCandidate = {
  searchFunctionId: SearchFunction['id']
  searchCandidateId: SearchCandidate['id']
  opsNotes: string
  isUseful: boolean | undefined
}
